import React, { useState } from 'react';
import { APIUrl } from '../../common/apiClient';
import { toast } from 'react-toastify';

export const ResetPassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Read token from the hash-based URL (e.g. #/reset-password?token=...) without relying on react-router hooks
  const token = React.useMemo(() => {
    try {
      const hash = window.location.hash || '';
      const queryIndex = hash.indexOf('?');
      const query = queryIndex >= 0 ? hash.substring(queryIndex) : '';
      const params = new URLSearchParams(query);
      return params.get('token') || '';
    } catch {
      return '';
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast.error('Invalid or missing reset token');
      return;
    }

    if (!newPassword || !confirmPassword) {
      toast.error('Please enter and confirm your new password');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(APIUrl + '/users/reset_password_with_token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.status || 'Password reset successfully. Please log in with your new password.');
        // Redirect to login page in react-admin (hash-based)
        window.location.hash = '#/login';
      } else {
        toast.error(data.status || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Failed to reset password', err);
      toast.error('An error occurred while resetting the password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #247695 0%, #0b3556 100%)',
        padding: '16px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          backgroundColor: '#ffffff',
          borderRadius: 8,
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          padding: '24px 24px 32px',
          boxSizing: 'border-box',
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 8, textAlign: 'center', color: '#123b63' }}>
          Reset Password
        </h2>
        <p style={{ marginTop: 0, marginBottom: 24, fontSize: 14, color: '#555', textAlign: 'center' }}>
          Enter your new password below. Make sure it is strong and unique.
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500 }}>
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                boxSizing: 'border-box',
                borderRadius: 4,
                border: '1px solid #ccd4dd',
                fontSize: 14,
              }}
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500 }}>
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                boxSizing: 'border-box',
                borderRadius: 4,
                border: '1px solid #ccd4dd',
                fontSize: 14,
              }}
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 4,
              border: 'none',
              backgroundColor: submitting ? '#7ba5c3' : '#247695',
              color: '#fff',
              fontSize: 15,
              fontWeight: 600,
              cursor: submitting ? 'default' : 'pointer',
              transition: 'background-color 0.2s ease',
            }}
          >
            {submitting ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
};
